import dayjs from 'dayjs';
import {z} from 'zod';

import i18n from '@/i18n';
import type {
  HealthLogMeasurement,
  HealthLogMeasurementInput,
  HealthLogMetricDefinition,
} from '@/services/graphqlHooks';
import {TIME_FORMATS} from '@/utils/timeFormats';

/***********************
 * Helpers
 ***********************/

const parseMeasurementValues = (
  rawValues: HealthLogMeasurement['values'] | null | undefined
): Record<string, number> => {
  if (!rawValues) return {};

  const parsed: unknown =
    typeof rawValues === 'string'
      ? (() => {
          try {
            return JSON.parse(rawValues) as unknown;
          } catch {
            return {};
          }
        })()
      : rawValues;

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};

  return Object.entries(parsed as Record<string, unknown>).reduce<
    Record<string, number>
  >((acc, [key, value]) => {
    const num = typeof value === 'number' ? value : Number(value);
    return Number.isNaN(num) ? acc : {...acc, [key]: num};
  }, {});
};

const mapFormValuesToNumericRecord = (
  values: Record<string, string>
): Record<string, number> =>
  Object.entries(values).reduce<Record<string, number>>((acc, [key, value]) => {
    if (value === '' || value == null) return acc;
    const num = Number(value);
    return Number.isNaN(num) ? acc : {...acc, [key]: num};
  }, {});

/***********************
 * Health Log Measurement Form
 ***********************/

export const healthLogMeasurementFormSchema = z
  .object({
    residentId: z.string().trim().min(1, i18n.t('Resident ID is required')),
    metricId: z.string().trim().min(1, i18n.t('Metric is required')),
    recordedAt: z.string().trim().min(1, i18n.t('Date and time are required')),
    values: z.record(
      z.string(),
      z
        .string(i18n.t('Value is required'))
        .trim()
        .min(1, i18n.t('Value is required'))
        .refine(
          v => v === '' || !Number.isNaN(Number(v)),
          i18n.t('Value must be a number')
        )
    ),
  })
  .refine(
    data =>
      dayjs(data.recordedAt, TIME_FORMATS.dateTimeHTMLLocal, true).isValid() ||
      dayjs(data.recordedAt).isValid(),
    {
      message: i18n.t('Invalid date and time'),
      path: ['recordedAt'],
    }
  )
  .refine(data => !dayjs(data.recordedAt).isAfter(dayjs()), {
    message: i18n.t('Recorded time cannot be in the future'),
    path: ['recordedAt'],
  })
  .refine(
    data =>
      Object.values(data.values).length > 0 &&
      Object.values(data.values).every(v => v !== '' && v != null),
    {
      message: i18n.t('All values are required'),
      path: ['values'],
    }
  );

export type HealthLogMeasurementFormValues = z.infer<
  typeof healthLogMeasurementFormSchema
>;

/***********************
 * Default values (immutable)
 ***********************/

export const healthLogMeasurementFormDefaultValues = (
  measurement?: Partial<HealthLogMeasurement>,
  metricDefinition?: HealthLogMetricDefinition
): HealthLogMeasurementFormValues => {
  const parsedValues = parseMeasurementValues(measurement?.values);

  const baseValues: Record<string, string> =
    metricDefinition?.fields && metricDefinition.fields.length > 0
      ? metricDefinition.fields.reduce<Record<string, string>>((acc, field) => {
          const existing = parsedValues[field.name];
          const value =
            typeof existing === 'number' && !Number.isNaN(existing)
              ? String(existing)
              : '';
          return {...acc, [field.name]: value};
        }, {})
      : Object.entries(parsedValues).reduce<Record<string, string>>(
          (acc, [key, value]) => ({
            ...acc,
            [key]: String(value),
          }),
          {}
        );

  return {
    residentId: measurement?.residentId || '',
    metricId: measurement?.metricId || '',
    recordedAt: measurement?.recordedAt || '',
    values: baseValues,
  };
};

/***********************
 * Mapping helper (immutable)
 ***********************/

export const mapHealthLogMeasurementFormValuesToMeasurementInput = (
  values: HealthLogMeasurementFormValues
): HealthLogMeasurementInput => {
  const numericValues = mapFormValuesToNumericRecord(values.values);

  return {
    residentId: values.residentId,
    metricId: values.metricId,
    recordedAt: dayjs(values.recordedAt).toISOString(),
    // AWSJSON scalar → typically sent as JSON string
    values: JSON.stringify(numericValues),
  };
};
